import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Badge from "../../components/common/Badge/Badge";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../components/common/Table/Table";
import ValidacionesHistorico from "../../components/common/ValidacionesHistorico/ValidacionesHistorico";
import "./FichaGestor.scss";
import DetalleLayout from "../../Layouts/DetalleLayout/DetalleLayout";
import { useZone } from "../../context/ZoneContext";
import { zonaFijaUsuario } from "../../utils/permisos";
import { getApiInstance } from "../../utils/api";
import { CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { CiCircleCheck } from "react-icons/ci";
import { IoEyeOutline } from "react-icons/io5";
import { MdOutlineFileDownload } from "react-icons/md";
import Notification from "../../components/common/notification/notification";
import { formatDate } from "../../utils/utils";
import { addOrUpdateRecentItem } from "../../utils/utils";
import { mapList, mappedDataToFormData } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import VisitaDetalleModal from "../../components/layout/Visitas/VisitaDetalleModal";
import {
    BADGE_VALORACION_VISITA,
    SECCIONES_INFORME_VISITA,
    VALORACIONES_VISITA,
    descargarInformeVisitaWord,
    formatFechaVisita,
    mapVisitas,
} from "../../components/layout/Visitas/visitaInforme";

const getTabsDatos = (t) => [
    { id: "residuos", label: t("managerSheet.waste") },
    { id: "documentacion", label: t("managerSheet.documentation") },
    { id: "facturas", label: t("managerSheet.invoices") },
    { id: "certificado", label: t("managerSheet.certificates") },
    { id: "tarifas", label: t("managerSheet.rates") },
    { id: "transporteGestionResiduos", label: t("managerSheet.wasteManagementTransport") },
    { id: "visitas", label: t("managerSheet.visits") },
];

export default function FichaGestor({ user }) {
    const { t } = useTranslation();
    const { id } = useParams();
    // Permite enlazar a una pestaña concreta, p.ej. desde Informes de visitas:
    // /gestores/123?tab=visitas
    const [searchParams] = useSearchParams();
    const tabInicialDatos = getTabsDatos(t).some(
        (tab) => tab.id === searchParams.get("tab")
    )
        ? searchParams.get("tab")
        : "documentacion";
    const [gestor, setGestor] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);
    const [tarifas, setTarifas] = useState([]);
    const [residuos, setResiduos] = useState([]);
    const [activeTabGeneral, setActiveTabGeneral] = useState("informacion");
    const [activeTabDatos, setActiveTabDatos] = useState(tabInicialDatos);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [certificados, setCertificados] = useState([]);
    const [facturas, setFacturas] = useState([]);
    const [transporteGestionResiduos, setTransporteGestionResiduos] =
        useState([]);
    const [isHistoricoModalOpen, setIsHistoricoModalOpen] = useState(false);
    const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
    const [visitas, setVisitas] = useState([]);
    const [visitaSeleccionada, setVisitaSeleccionada] = useState(null);
    const [isVisitaModalOpen, setIsVisitaModalOpen] = useState(false);
    const notificationRef = useRef(null);
    const { selectedZone } = useZone();

    const getFormElementsDocumentosResiduos = () => {
        const codZonaLegal = selectedZone.value;
        const codTipoAutorizacion =
            codZonaLegal === "1" ? 18 : codZonaLegal === "2" ? 20 : 18;

        return [
            {
                titleVisible: false,
                elements: [
                    {
                        name: "codProveedor",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                        defaultValue: `${id}`,
                    },
                    {
                        name: "codtipoDocumento",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                    },
                    {
                        name: "sectionObligatoria",
                        type: "section",
                        label: t("managerSheet.mandatoryDocumentationColon"),
                        visible: true,
                    },
                    {
                        name: "autorizacionTratamiento",
                        type: "checkbox",
                        label: t("managerSheet.wasteTreatmentAuthorization"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                        exclusiveGroup: "tipoDocumentacionResiduos",
                        valueMapField: "codtipoDocumento",
                        valueWhenChecked: codTipoAutorizacion,
                    },
                    {
                        name: "sectionNoObligatoria",
                        type: "section",
                        label: t("managerSheet.nonMandatoryDocumentationColon"),
                        visible: true,
                    },
                    {
                        name: "certificadoISO14001",
                        type: "checkbox",
                        label: t("managerSheet.iso14001Certificates"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                        exclusiveGroup: "tipoDocumentacionResiduos",
                        valueMapField: "codtipoDocumento",
                        valueWhenChecked: 19,
                    },
                    {
                        name: "nima",
                        type: "checkbox",
                        label: "NIMA",
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                        exclusiveGroup: "tipoDocumentacionResiduos",
                        valueMapField: "codtipoDocumento",
                        valueWhenChecked: 5,
                    },
                    {
                        name: "codigoAutorizacion",
                        type: "text",
                        label: t("managerSheet.authorizationCode"),
                        placeholder: t("managerSheet.enterTheAuthorizationCode"),
                        required: true,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                        visibleWhen: {
                            field: "autorizacionTratamiento",
                            value: true,
                        },
                    },
                    {
                        name: "fechaIni",
                        type: "date",
                        label: t("managerSheet.startDate"),
                        placeholder: t("managerSheet.selectDate"),
                        required: true,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "fechaFin",
                        type: "date",
                        label: t("managerSheet.endDate"),
                        placeholder: t("managerSheet.selectDate"),
                        required: true,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "descripcion",
                        type: "textarea",
                        label: t("managerSheet.description"),
                        placeholder: t("managerSheet.enterADescription"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                        rows: 3,
                    },
                    {
                        name: "fichero",
                        type: "document",
                        label: t("managerSheet.uploadDocument"),
                        placeholder: t("managerSheet.selectAFile"),
                        required: true,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "codZona",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                        /* La zona del usuario; solo el Administrador global usa
                           la zona seleccionada. */
                        defaultValue: `${zonaFijaUsuario(user) ?? selectedZone.value}`,
                    },
                    {
                        name: "codZonaLegal",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                        defaultValue: `${selectedZone.value}`,
                    },
                ],
            },
        ];
    };

    const getFormElementsDocumentos = (tipo) => {
        const codPais = gestor?.codPais;

        let codTipoDocumentoValue = null;
        if (tipo === "factura") {
            if (codPais === 2) {
                codTipoDocumentoValue = 15;
            } else if (codPais === 3) {
                codTipoDocumentoValue = 22;
            } else {
                codTipoDocumentoValue = 22;
            }
        } else if (tipo === "certificado") {
            codTipoDocumentoValue = 14;
        }

        const isDocumento = tipo === "documento";

        return [
            {
                elements: [
                    {
                        name: "codProveedor",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                        defaultValue: `${id}`,
                    },
                    {
                        name: "codtipoDocumento",
                        type: "select",
                        label: t("managerSheet.documentType"),
                        placeholder: t("managerSheet.selectTheDocumentType"),
                        required: true,
                        disabled: !isDocumento, // deshabilitado si no es documento
                        labelVisible: true,
                        visible: isDocumento, // oculto si es factura o certificado
                        options: tiposDocumentos,
                        defaultValue: codTipoDocumentoValue ?? undefined,
                    },
                    {
                        name: "descripcion",
                        type: "text",
                        label: t("managerSheet.fileName"),
                        placeholder: t("managerSheet.enterTheFileName"),
                        required: isDocumento,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "fechaIni",
                        type: "date",
                        label: t("managerSheet.validityDate"),
                        required: isDocumento,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "fechaFin",
                        type: "date",
                        label: t("managerSheet.expiryDate"),
                        required: isDocumento,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "fichero",
                        type: "document",
                        label: t("managerSheet.file"),
                        placeholder: t("managerSheet.selectAFile"),
                        required: true,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "observaciones",
                        type: "textarea",
                        label: t("managerSheet.observations"),
                        placeholder: t("managerSheet.enterAdditionalObservations"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                        rows: 3,
                    },
                    {
                        name: "codZona",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                        /* La zona del usuario; solo el Administrador global usa
                           la zona seleccionada. */
                        defaultValue: `${zonaFijaUsuario(user) ?? selectedZone.value}`,
                    },
                    {
                        name: "codZonaLegal",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                        defaultValue: `${selectedZone.value}`,
                    },
                ],
            },
        ];
    };

    // El informe se reparte en cuatro pasos: FormModal muestra un grupo cada
    // vez y navega con las flechas del titulo, asi que el panel lateral ya no
    // tiene que ensancharse para que quepan las siete secciones de texto.
    const getFormElementsVisita = () => {
        const seccionesTexto = SECCIONES_INFORME_VISITA.map((seccion) => ({
            name: seccion.key,
            type: "textarea",
            label: seccion.label,
            placeholder: t("managerSheet.writeHere", { section: seccion.label.toLowerCase() }),
            required: false,
            disabled: false,
            labelVisible: true,
            visible: true,
        }));

        return [
            {
                title: t("managerSheet.visitData"),
                titleVisible: true,
                elements: [
                    {
                        name: "codProveedor",
                        type: "text",
                        required: true,
                        disabled: false,
                        visible: false,
                        defaultValue: `${id}`,
                    },
                    {
                        name: "fechaVisita",
                        type: "date",
                        label: t("managerSheet.visitDate"),
                        required: true,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "titulo",
                        type: "text",
                        label: t("managerSheet.reportTitle"),
                        placeholder:
                            t("managerSheet.eGVisitReportToWasteManager"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "ubicacion",
                        type: "text",
                        label: t("managerSheet.location"),
                        placeholder: t("managerSheet.eGDaconOurense"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "tipoInstalacion",
                        type: "text",
                        label: t("managerSheet.facilityType"),
                        placeholder:
                            t("managerSheet.eGConstructionAndDemolitionWasteCdw"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "tecnico",
                        type: "text",
                        label: t("managerSheet.technicianPerformingTheVisit"),
                        placeholder: t("managerSheet.fullName"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                    {
                        name: "codValoracion",
                        type: "select",
                        label: t("managerSheet.rating"),
                        placeholder: t("managerSheet.selectTheVisitRating"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                        options: VALORACIONES_VISITA,
                    },
                ],
            },
            {
                title: t("managerSheet.reportContent12"),
                titleVisible: true,
                elements: seccionesTexto.slice(0, 4),
            },
            {
                title: t("managerSheet.reportContent22"),
                titleVisible: true,
                elements: seccionesTexto.slice(4),
            },
            {
                title: t("managerSheet.document"),
                titleVisible: true,
                elements: [
                    {
                        name: "fichero",
                        type: "document",
                        label: t("managerSheet.attachmentOptionalWhenEditingLeaveItEmpty"),
                        placeholder: t("managerSheet.selectAFile"),
                        required: false,
                        disabled: false,
                        labelVisible: true,
                        visible: true,
                    },
                ],
            },
        ];
    };

    useEffect(() => {
        if (gestor != null) {
            addOrUpdateRecentItem({
                id: gestor.codProveedor,
                title: gestor.nombre,
                type: "gestores",
            });
        }
    }, [gestor]);

    useEffect(() => {
        if (gestor != null) {
            addOrUpdateRecentItem({
                id: gestor.codProveedor,
                title: gestor.nombre,
                type: "gestores",
            });
        }
    }, [gestor]);

    const fetchGestor = async (id) => {
        try {
            const api = getApiInstance();
            console.log(`Fetching gestor with ID: ${id}`);
            const response = await api.get(
                `/api/Proveedores/GetProveedorById/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentos = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/GestorDocumentos/GetDocumentosFromGestor/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const handleDocumentosUpdate = useCallback(async () => {
        try {
            const data = await fetchDocumentos(id);
            const codDocumento = (doc) => Number(doc.codTipoDocumento);
            setDocumentos(
                data.filter(
                    (doc) =>
                        ![15, 22, 14].includes(codDocumento(doc))
                )
            );
            setCertificados(
                data.filter((doc) => codDocumento(doc) === 14)
            );
            setFacturas(
                data.filter((doc) =>
                    [15, 22].includes(codDocumento(doc))
                )
            );
        } catch (error) {
            console.error("Error refrescando documentos:", error);
        }
    }, [id]);

    const fetchVisitas = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ProveedoresVisitas/GetVisitasFromGestor/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching visitas:", error);
            throw error;
        }
    };

    const handleVisitasUpdate = useCallback(async () => {
        try {
            const data = await fetchVisitas(id);
            setVisitas(mapVisitas(data));
        } catch (error) {
            console.error("Error refrescando visitas:", error);
        }
    }, [id]);

    const handleVerVisita = (visita) => {
        setVisitaSeleccionada(visita);
        setIsVisitaModalOpen(true);
    };

    // Word de un informe suelto, el mismo que ofrece el modal de detalle
    const handleExportarVisitaWord = async (visita) => {
        try {
            await descargarInformeVisitaWord(visita);
        } catch (err) {
            console.error("Error exportando el informe de visita a Word:", err);
            notificationRef.current?.addNotification(
                t("managerSheet.couldNotGenerateTheWordFileOf"),
                "error"
            );
        }
    };

    const fetchTarifas = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Tarifa/GetTarifasByProveedor/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchResiduos = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Proveedores/GetResiduosByProveedor/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchTransporteGestionResiduos = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ResiduosObra/GetTransporteGestionResiduosByProveedor/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error(
                "Error fetching transporte gestión residuos:",
                error
            );
            throw error;
        }
    };

    const handleVerValidaciones = (row) => {
        setDocumentoSeleccionado(row);
        setIsHistoricoModalOpen(true);
    };

    const fetchTiposDocumentos = async () => {
        try {
            // Productor = 1,
            // Gestor = 2,
            // Obra = 3,
            const api = getApiInstance();
            const response = await api.get(
                `/api/Documentos/GetTiposDocumentosByTipoEntidad/2`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching tipos documentos:", error);
            throw error;
        }
    };

    useEffect(() => {
        const loadGestorData = async () => {
            try {
                setLoading(true);
                setError(null);

                const results = await Promise.allSettled([
                    fetchGestor(id),
                    fetchDocumentos(id),
                    fetchTarifas(id),
                    fetchTiposDocumentos(),
                    fetchResiduos(id),
                    fetchTransporteGestionResiduos(id),
                    fetchVisitas(id),
                ]);

                const [
                    gestorRes,
                    docRes,
                    tarifaRes,
                    tiposDocRes,
                    residuosRes,
                    transporteGestionResiduosRes,
                    visitasRes,
                ] = results;

                const gestorData =
                    gestorRes.status === "fulfilled" ? gestorRes.value : null;
                const documentosData =
                    docRes.status === "fulfilled" ? docRes.value : [];
                const tarifasData =
                    tarifaRes.status === "fulfilled" ? tarifaRes.value : [];
                const residuosData =
                    residuosRes.status === "fulfilled" ? residuosRes.value : [];
                const tiposDocumentosData =
                    tiposDocRes.status === "fulfilled" ? tiposDocRes.value : [];
                const transporteGestionResiduosData =
                    transporteGestionResiduosRes.status === "fulfilled"
                        ? transporteGestionResiduosRes.value
                        : [];
                const visitasData =
                    visitasRes.status === "fulfilled" ? visitasRes.value : [];

                setGestor(gestorData);
                setVisitas(mapVisitas(visitasData));

                const codDocumento = (doc) => Number(doc.codTipoDocumento);

                setDocumentos(
                    documentosData.filter(
                        (doc) =>
                            codDocumento(doc) !== 15 &&
                            codDocumento(doc) !== 22 &&
                            codDocumento(doc) !== 14
                    )
                ); // Documentación: excluimos facturas (15/22) y certificados (14)
                setTarifas(tarifasData);
                setResiduos(residuosData);
                setCertificados(
                    documentosData.filter(
                        (doc) => codDocumento(doc) === 14
                    )
                ); // solo certificados
                setFacturas(
                    documentosData.filter(
                        (doc) =>
                            codDocumento(doc) === 15 ||
                            codDocumento(doc) === 22
                    )
                ); // solo facturas
                setTransporteGestionResiduos(transporteGestionResiduosData);
                setTiposDocumentos([
                    ...mapList(
                        tiposDocumentosData,
                        "descripcion",
                        "codTipoDocumento"
                    ),
                ]);

                // opcional: si gestorRes rechazó por algo distinto a 404, mostrarlo
                if (gestorRes.status === "rejected") {
                    /* 403: el gestor es de otra zona y el backend no deja abrirlo. */
                    setError(
                        gestorRes.reason?.response?.status === 403
                            ? t("managerSheet.noAccess")
                            : gestorRes.reason?.message ||
                                  t("managerSheet.errorLoading")
                    );
                }
            } catch (err) {
                console.error("Error cargando datos del gestor:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) loadGestorData();
    }, [id]);

    const getEstadoBadge = (estado) => {
        const num = String(estado);
        const estadoConfig = {
            "0": { type: "info", label: t("managerSheet.noDocument"), Icon: CiClock1 },
            "1": { type: "info", label: t("managerSheet.pending"), Icon: CiClock1 },
            "2": { type: "warning", label: t("managerSheet.incident"), Icon: CgDanger },
            "3": { type: "success", label: t("managerSheet.validated"), Icon: CiCircleCheck },
            "4": { type: "danger", label: t("managerSheet.expired"), Icon: CiCircleCheck },
            VALIDADO: {
                type: "success",
                label: t("managerSheet.validated"),
                Icon: CiCircleCheck,
            },
            PENDIENTE: { type: "info", label: t("managerSheet.pending"), Icon: CiClock1 },
            INCIDENCIA: {
                type: "warning",
                label: t("managerSheet.incident"),
                Icon: CgDanger,
            },
            VENCIDO: { type: "danger", label: t("managerSheet.expired2"), Icon: CgDanger },
            ACTIVA: { type: "success", label: t("managerSheet.active"), Icon: CiCircleCheck },
        };
        return estadoConfig[num] || estadoConfig[estado] || estadoConfig["1"];
    };

    const tabsGeneral = [
        { id: "informacion", label: t("managerSheet.generalInformation") },
    ];

    const tabsDatos = getTabsDatos(t);

    const renderInformacionGeneral = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.nameColon")}</label>
                <span>{gestor.nombre}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.taxIdColon")}</label>
                <span>{gestor.cif}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.contactColon")}</label>
                <span>{gestor.contacto}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.phoneColon")}</label>
                <span>{gestor.telefono}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.emailColon")}</label>
                <span>{gestor.email}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.addressColon")}</label>
                <span>{gestor.direccion}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.localityColon")}</label>
                <span>{gestor.localidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.postalCodeColon")}</label>
                <span>{gestor.codigoPostal}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.countryColon")}</label>
                <span>{gestor.pais}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.communityColon")}</label>
                <span>{gestor.comunidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.provinceColon")}</label>
                <span>{gestor.provincia}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.statusColon")}</label>
                <span className="ficha-gestor__field-badge">
                    <Badge
                        variant={getEstadoBadge(gestor.codEstado).type}
                        Icon={getEstadoBadge(gestor.codEstado).Icon}
                    >
                        {getEstadoBadge(gestor.codEstado).label}
                    </Badge>
                </span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("managerSheet.typeColon")}</label>
                <span>{gestor.tipo}</span>
            </div>
            {/* Los NIMA solo se rellenan en gestores de España */}
            {gestor.nimaTransporte && (
                <div className="ficha-gestor__field">
                    <label>{t("managerSheet.nimaTransportColon")}</label>
                    <span>{gestor.nimaTransporte}</span>
                </div>
            )}
            {gestor.nimaGestionFinal && (
                <div className="ficha-gestor__field">
                    <label>{t("managerSheet.nimaFinalManagementColon")}</label>
                    <span>{gestor.nimaGestionFinal}</span>
                </div>
            )}
        </div>
    );

    const renderResiduos = () => (
        <div className="ficha-gestor__residuos">
            <h3 className="ficha-gestor__residuos-title">
                {t("managerSheet.authorizedWaste")}
            </h3>
            <Table
                columns={[
                    {
                        key: "codResiduo",
                        label: "ID",
                        visible: false,
                    },
                    {
                        key: "codigo",
                        label: t("managerSheet.workCode"),
                        visible: true,
                        sortable: true,
                    },
                    {
                        key: "nombre",
                        label: t("managerSheet.waste2"),
                        visible: true,
                        sortable: true,
                        visibleOnExpand: true,
                    },
                ]}
                dataTable={residuos}
                noResultsMessage={t("managerSheet.noWasteAvailable")}
                rowsPerPage={10}
                showCheckboxes={false}
                elementName={t("elements.waste")}
                stateProp="codEstado"
                searchTerms={["codigo", "nombre"]}
            ></Table>
        </div>
    );

    const renderVisitas = () => (
        <div className="ficha-gestor__visitas">
            <Table
                columns={[
                    {
                        key: "fechaVisita",
                        label: t("managerSheet.date"),
                        visible: true,
                        sortable: true,
                        render: (row) => formatFechaVisita(row.fechaVisita),
                    },
                    {
                        key: "titulo",
                        label: t("managerSheet.report"),
                        visible: true,
                        sortable: true,
                        bold: true,
                    },
                    {
                        key: "tecnico",
                        label: t("managerSheet.technician"),
                        visible: true,
                        sortable: true,
                    },
                    {
                        key: "valoracion",
                        label: t("managerSheet.rating"),
                        visible: true,
                        sortable: true,
                        render: (row) => {
                            const badge =
                                BADGE_VALORACION_VISITA[row.codValoracion];
                            if (!badge) return "-";
                            return (
                                <Badge variant={badge.type} Icon={badge.Icon}>
                                    {badge.label}
                                </Badge>
                            );
                        },
                    },
                    {
                        key: "documento",
                        label: t("managerSheet.attachment"),
                        visible: true,
                        sortable: false,
                        type: "document",
                        documentProp: "ficheroBase64",
                    },
                ]}
                dataTable={visitas}
                onDataUpdate={handleVisitasUpdate}
                keyProp="codVisita"
                sortProp="fechaVisita"
                searchTerms={["titulo", "tecnico", "ubicacion"]}
                noResultsMessage={t("managerSheet.noVisitsRecordedYet")}
                rowsPerPage={10}
                showCheckboxes={false}
                elementName={t("elements.visit")}
                stateProp=""
                actions={[
                    {
                        label: t("managerSheet.viewReport"),
                        action: (row) => handleVerVisita(row),
                        icon: <IoEyeOutline size={16} />,
                    },
                    {
                        label: t("managerSheet.exportToWord"),
                        action: (row) => handleExportarVisitaWord(row),
                        icon: <MdOutlineFileDownload size={16} />,
                    },
                    "Editar",
                    "Eliminar",
                ]}
                formElements={getFormElementsVisita()}
                modalAddTitle={t("managerSheet.newVisitReport")}
                addApiUrl="/api/ProveedoresVisitas/SetVisita"
                editApiUrl="/api/ProveedoresVisitas/UpdateVisita"
                deleteApiUrl="/api/ProveedoresVisitas/DeleteVisita"
                // El adjunto es opcional: sin este mapper la tabla enviaría JSON
                // cuando no hay fichero y multipart cuando lo hay.
                payloadMapper={(payload) =>
                    payload instanceof FormData
                        ? payload
                        : mappedDataToFormData(payload)
                }
                successMessages={{
                    add: t("managerSheet.visitCreated"),
                    edit: t("managerSheet.visitUpdated"),
                    delete: t("managerSheet.visitDeleted"),
                }}
                loading={loading}
                userCodAcceso={Number(user.codAcceso)}
                userCodPerfil={Number(user.role)}
                excludeFields={["titulo", "codVisita", "codProveedor", "codObra", "introduccion", "objetivos", "descripcion", "conclusiones", "descripcionInstalacion", "observaciones", "ficheroBase64", "fichero", "documento", "codUsuario", "desarrollo", "recomendaciones", "valoracion", "proveedor","fechaAlta"]}
            />
        </div>
    );

    const renderGeneralContent = () => {
        switch (activeTabGeneral) {
            default:
                return renderInformacionGeneral();
        }
    };

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "residuos":
                return renderResiduos();
            case "documentacion":
                return (
                    <div className="ficha-gestor__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "tipoDocumento",
                                    label: t("managerSheet.documentType"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "documento",
                                    label: t("managerSheet.document"),
                                    visible: true,
                                    sortable: true,
                                    type: "document",
                                    documentProp: "ficheroBase64",
                                },
                                {
                                    key: "validez",
                                    label: t("managerSheet.validity"),
                                    visible: true,
                                    sortable: true,
                                    render: (row) =>
                                        `${formatDate(
                                            row.fechaIni
                                        )} - ${formatDate(row.fechaFin)}`,
                                },
                                {
                                    key: "codEstado",
                                    label: t("managerSheet.validation"),
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={documentos}
                            onDataUpdate={handleDocumentosUpdate}
                            keyProp="codDocumento"
                            searchTerms={[
                                "tipoDocumento",
                                "documento",
                                "validez",
                            ]}
                            noResultsMessage={t("managerSheet.noDocumentsAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={[
                                {
                                    label: t("managerSheet.validate"),
                                    actionType: "validate",
                                    icon: <CiCircleCheck size={16} />,
                                },
                                {
                                    label: t("managerSheet.viewValidations"),
                                    action: (row) =>
                                        handleVerValidaciones(row),
                                    icon: <CiClock1 size={16} />,
                                },
                                "Editar",
                                "Eliminar",
                            ]}
                            elementName={t("elements.document")}
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "", Icon: null },
                                {
                                    type: "info",
                                    label: t("managerSheet.pendingValidation"),
                                    Icon: CiClock1,
                                },
                                {
                                    type: "warning",
                                    label: t("managerSheet.validationWithIncidents"),
                                    Icon: CgDanger,
                                },
                                {
                                    type: "success",
                                    label: t("managerSheet.validated"),
                                    Icon: CiCircleCheck,
                                },
                                {
                                    type: "danger",
                                    label: t("managerSheet.expired"),
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            stateChangeOptions={[
                                { value: "1", label: t("managerSheet.pendingValidation") },
                                {
                                    value: "2",
                                    label: t("managerSheet.validationWithIncidents"),
                                },
                                { value: "3", label: t("managerSheet.validated") },
                                { value: "4", label: t("managerSheet.expired") },
                            ]}
                            stateChangeUrl="/api/Validaciones/SetValidacion"
                            formElements={getFormElementsDocumentosResiduos()}
                            modalAddTitle={t("managerSheet.linkDocument")}
                            addApiUrl="/api/GestorDocumentos/SetDocumento"
                            editApiUrl="/api/GestorDocumentos/UpdateDocumento"
                            deleteApiUrl="/api/GestorDocumentos/DeleteDocumento"
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            /* El intermediario añade, edita y borra documentos
                               del gestor, como en la antigua
                               (Proveedores/Details.cshtml). Validar no. */
                            intermediarioPuedeEscribir
                        />
                    </div>
                );
            case "tarifas":
                return (
                    <div className="ficha-gestor__tarifas">
                        <Table
                            columns={[
                                {
                                    key: "descr",
                                    label: t("managerSheet.description"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "fechaInicio",
                                    label: t("managerSheet.startDate"),
                                    visible: true,
                                    sortable: true,
                                    type: "date",
                                },
                                {
                                    key: "fechaFin",
                                    label: t("managerSheet.endDate"),
                                    visible: true,
                                    sortable: true,
                                    type: "date",
                                },
                            ]}
                            dataTable={tarifas}
                            onDataUpdate={(newData) => setTarifas(newData)}
                            keyProp="codTarifa"
                            searchTerms={["descr", "fechaInicio", "fechaFin"]}
                            noResultsMessage={t("managerSheet.noRatesAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={["Editar", "Eliminar"]}
                            elementName={t("elements.rate")}
                            formElements={[
                                {
                                    elements: [
                                        {
                                            name: "codProveedor",
                                            type: "text",
                                            required: true,
                                            disabled: false,
                                            visible: false,
                                            defaultValue: Number(id),
                                        },
                                        {
                                            name: "proveedor",
                                            type: "text",
                                            label: t("managerSheet.supplier"),
                                            required: true,
                                            disabled: true,
                                            visible: true,
                                            defaultValue: `${gestor?.nombre}`,
                                        },
                                        {
                                            name: "descr",
                                            type: "textarea",
                                            label: t("managerSheet.description"),
                                            placeholder:
                                                t("managerSheet.enterADescription"),
                                            required: false,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                            rows: 3,
                                        },
                                        {
                                            name: "fechaInicio",
                                            type: "date",
                                            label: t("managerSheet.startDate2"),
                                            placeholder: t("managerSheet.selectDate"),
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                        {
                                            name: "fechaFin",
                                            type: "date",
                                            label: t("managerSheet.endDate2"),
                                            placeholder: t("managerSheet.selectDate"),
                                            required: true,
                                            disabled: false,
                                            labelVisible: true,
                                            visible: true,
                                        },
                                    ],
                                },
                            ]}
                            addApiUrl="/api/Tarifa/SetTarifa"
                            editApiUrl="/api/Tarifa/EditTarifa"
                            deleteApiUrl="/api/Tarifa/DeleteTarifa"
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                        />
                    </div>
                );
            case "certificado":
                return (
                    <div className="ficha-gestor__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "documento",
                                    label: t("managerSheet.document"),
                                    visible: true,
                                    sortable: true,
                                    type: "document",
                                    documentProp: "ficheroBase64",
                                },
                                {
                                    key: "validez",
                                    label: t("managerSheet.validity"),
                                    visible: true,
                                    sortable: true,
                                    render: (row) =>
                                        `${formatDate(
                                            row.fechaIni
                                        )} - ${formatDate(row.fechaFin)}`,
                                },
                                {
                                    key: "codEstado",
                                    label: t("managerSheet.validation"),
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={certificados}
                            onDataUpdate={(newData) => setCertificados(newData)}
                            keyProp="codDocumento"
                            searchTerms={["documento", "validez"]}
                            noResultsMessage={t("managerSheet.noCertificatesAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={[
                                {
                                    label: t("managerSheet.validate"),
                                    actionType: "validate",
                                    icon: <CiCircleCheck size={16} />,
                                },
                                {
                                    label: t("managerSheet.viewValidations"),
                                    action: (row) =>
                                        handleVerValidaciones(row),
                                    icon: <CiClock1 size={16} />,
                                },
                                "Eliminar",
                            ]}
                            elementName={t("elements.certificate")}
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "", Icon: null },
                                {
                                    type: "info",
                                    label: t("managerSheet.pendingValidation"),
                                    Icon: CiClock1,
                                },
                                {
                                    type: "warning",
                                    label: t("managerSheet.validationWithIncidents"),
                                    Icon: CgDanger,
                                },
                                {
                                    type: "success",
                                    label: t("managerSheet.validated"),
                                    Icon: CiCircleCheck,
                                },
                                {
                                    type: "danger",
                                    label: t("managerSheet.expired"),
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            stateChangeOptions={[
                                { value: "1", label: t("managerSheet.pendingValidation") },
                                {
                                    value: "2",
                                    label: t("managerSheet.validationWithIncidents"),
                                },
                                { value: "3", label: t("managerSheet.validated") },
                                { value: "4", label: t("managerSheet.expired") },
                            ]}
                            stateChangeUrl="/api/Validaciones/SetValidacion"
                            formElements={getFormElementsDocumentos(
                                "certificado"
                            )}
                            addApiUrl="/api/GestorDocumentos/SetDocumento"
                            deleteApiUrl="/api/GestorDocumentos/DeleteDocumento"
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            /* El intermediario añade, edita y borra documentos
                               del gestor, como en la antigua
                               (Proveedores/Details.cshtml). Validar no. */
                            intermediarioPuedeEscribir
                        />
                    </div>
                );
            case "facturas":
                return (
                    <div className="ficha-gestor__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "documento",
                                    label: t("managerSheet.document"),
                                    visible: true,
                                    sortable: true,
                                    type: "document",
                                    documentProp: "ficheroBase64",
                                },
                                {
                                    key: "validez",
                                    label: t("managerSheet.validity"),
                                    visible: true,
                                    sortable: true,
                                    render: (row) =>
                                        `${formatDate(
                                            row.fechaIni
                                        )} - ${formatDate(row.fechaFin)}`,
                                },
                                {
                                    key: "codEstado",
                                    label: t("managerSheet.validation"),
                                    visible: true,
                                    sortable: true,
                                },
                            ]}
                            dataTable={facturas}
                            onDataUpdate={(newData) => setFacturas(newData)}
                            keyProp="codDocumento"
                            searchTerms={["documento", "validez"]}
                            noResultsMessage={t("managerSheet.noInvoicesAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            actions={[
                                {
                                    label: t("managerSheet.validate"),
                                    actionType: "validate",
                                    icon: <CiCircleCheck size={16} />,
                                },
                                {
                                    label: t("managerSheet.viewValidations"),
                                    action: (row) =>
                                        handleVerValidaciones(row),
                                    icon: <CiClock1 size={16} />,
                                },
                                "Eliminar",
                            ]}
                            elementName={t("elements.invoice")}
                            stateProp="codEstado"
                            states={[
                                { type: "primary", label: "", Icon: null },
                                {
                                    type: "info",
                                    label: t("managerSheet.pendingValidation"),
                                    Icon: CiClock1,
                                },
                                {
                                    type: "warning",
                                    label: t("managerSheet.validationWithIncidents"),
                                    Icon: CgDanger,
                                },
                                {
                                    type: "success",
                                    label: t("managerSheet.validated"),
                                    Icon: CiCircleCheck,
                                },
                                {
                                    type: "danger",
                                    label: t("managerSheet.expired"),
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            stateChangeOptions={[
                                { value: "1", label: t("managerSheet.pendingValidation") },
                                {
                                    value: "2",
                                    label: t("managerSheet.validationWithIncidents"),
                                },
                                { value: "3", label: t("managerSheet.validated") },
                                { value: "4", label: t("managerSheet.expired") },
                            ]}
                            stateChangeUrl="/api/Validaciones/SetValidacion"
                            formElements={getFormElementsDocumentos("factura")}
                            addApiUrl="/api/GestorDocumentos/SetDocumento"
                            deleteApiUrl="/api/GestorDocumentos/DeleteDocumento"
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            /* El intermediario añade, edita y borra documentos
                               del gestor, como en la antigua
                               (Proveedores/Details.cshtml). Validar no. */
                            intermediarioPuedeEscribir
                        />
                    </div>
                );
            case "transporteGestionResiduos":
                return (
                    <div className="ficha-gestor__transporte-gestion-residuos">
                        <Table
                            columns={[
                                {
                                    key: "codObra",
                                    label: t("managerSheet.productionCenter"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "cantidadTransporte",
                                    label: t("managerSheet.quantitiesByTransportKg"),
                                    visible: true,
                                    sortable: true,
                                    type: "number",
                                    render: (row) =>
                                        `${new Intl.NumberFormat(
                                            "es-ES"
                                        ).format(row.cantidadTransporte ?? 0)} kg`,
                                },
                                {
                                    key: "cantidadGestionFinal",
                                    label: t("managerSheet.quantitiesByFinalDestinationKg"),
                                    visible: true,
                                    sortable: true,
                                    type: "number",
                                    render: (row) =>
                                        `${new Intl.NumberFormat(
                                            "es-ES"
                                        ).format(
                                            row.cantidadGestionFinal ?? 0
                                        )} kg`,
                                },
                                {
                                    key: "cantidadTotal",
                                    label: t("managerSheet.generatedWasteQuantitiesKg"),
                                    visible: true,
                                    sortable: true,
                                    type: "number",
                                    render: (row) =>
                                        `${new Intl.NumberFormat(
                                            "es-ES"
                                        ).format(
                                            row.cantidadTotal ?? 0
                                        )} kg`,
                                },
                            ]}
                            dataTable={transporteGestionResiduos}
                            keyProp="codProductor"
                            searchTerms={[
                                "codProductor",
                                "cantidadTransporte",
                                "cantidadDestinoFinal",
                                "cantidadResiduoGenerada",
                            ]}
                            noResultsMessage={t("managerSheet.noWasteManagementTransportDataAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            loading={loading}
                            elementName={t("elements.wasteManagementTransport")}
                            stateProp="codEstado"
                        />
                    </div>
                );
            case "visitas":
                return renderVisitas();
            default:
                return null;
        }
    };

    return (
        <DetalleLayout
            title={gestor ? t("managerSheet.titleN", { name: gestor.nombre }) : t("common.loading")}
            backLink="/gestores"
            backText={t("backTo.managers")}
            loading={loading}
            error={error}
            notFound={!gestor && !loading && !error}
            className="ficha-gestor"
        >
            {!loading && !error && gestor && (
                <div className="ficha-gestor__content">
                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>{t("managerSheet.generalInformation")}</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsGeneral}
                            activeTab={activeTabGeneral}
                            onTabChange={setActiveTabGeneral}
                            noBorderRadius={true}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderGeneralContent()}
                        </div>
                    </div>

                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>{t("managerSheet.managerData")}</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                            noBorderRadius={true}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                </div>
            )}

            {/* Documentos, certificados y facturas del gestor comparten el tipo
                de validación 7 (ProveedoresDocumentos) y la clave codDocumento. */}
            <ValidacionesHistorico
                isOpen={isHistoricoModalOpen}
                onClose={() => setIsHistoricoModalOpen(false)}
                codTipoValidacion={7}
                codClave={
                    documentoSeleccionado?.CodClave ??
                    documentoSeleccionado?.codDocumento
                }
            />

            <VisitaDetalleModal
                isOpen={isVisitaModalOpen}
                onClose={() => setIsVisitaModalOpen(false)}
                visita={visitaSeleccionada}
                gestorNombre={gestor?.nombre}
            />

            <Notification ref={notificationRef} />
        </DetalleLayout>
    );
}
